import React, { useState } from "react";
import classNames from "classnames";
import { Link, useLocation } from "react-router-dom";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import {
    Navbar,
    NavbarToggler,
    NavbarBrand,
    Nav,
    NavItem,
    NavLink,
} from "reactstrap";

import { User, Organisation } from "@auth/models";
import { LogoutButton } from "@auth/components/LogoutButton";
import { messages } from "@app/messages";
import { messages as authMessages } from "@auth/messages";
import { messages as deliveryMessages } from "@deliveries/messages";
import { messages as reportsMessages } from "@reports/messages";
import { messages as salesMessages } from "@sales/messages";
import { LanguageSelect } from "@localisation/components/LanguageSelect";
import { Offcanvas } from "@ui/Offcanvas";
import { WaIcon } from "@ui/WaIcon";
import { messages as uiMessages } from "@ui/messages";
import { Search } from "@search/components/Search";

type FormatMessageFn = (
    messageDescriptor: MessageDescriptor,
    values?: {[key: string]: string | number | boolean | Date},
) => string;
interface AuthenticatedNavProps {
    canCreateAlbum: boolean;
    canViewUser: boolean;
    canViewDelivery: boolean;
    canViewReport: boolean;
    canGenerateReport: boolean;
    canViewBundle: boolean;
    canViewPlaylist: boolean;
    canViewGlobalStats: boolean;
    canViewSales: boolean;
    user: User;
    organisation: Organisation;
    formatMessage: FormatMessageFn;
}

export const AuthenticatedNav = ({
    canCreateAlbum,
    canViewUser,
    canViewDelivery,
    canViewReport,
    canGenerateReport,
    // canViewBundle,
    canViewPlaylist,
    canViewGlobalStats,
    canViewSales,
    user,
    organisation,
    formatMessage,
}: AuthenticatedNavProps) => {
    const location = useLocation();
    // Handle the mobile menu
    const [ menuOpen, setMenuOpen ] = useState(false);
    const toggleMenu = () => setMenuOpen(!menuOpen);
    const [ searchOpen, setSearchOpen ] = useState(false);
    const toggleSearch = () => setSearchOpen(!searchOpen);

    const baseLinkProps = {
        onClick: toggleMenu,
    };
    const indexProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/",
        className: location.pathname === "/" ? "active" : undefined,
    };
    const albumCreateProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/albums/create",
        title: formatMessage(messages.navCreateAlbumTitle),
        className: location.pathname === "/albums/create" ? "active" : undefined,
    };
    const usersProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/users",
        title: formatMessage(authMessages.userEntityPlural),
        className: location.pathname.startsWith("/users") ? "active" : undefined,
    };
    const statementsProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/statements",
        title: formatMessage(reportsMessages.statementPlural),
        className: location.pathname === "/statements" ? "active" : undefined,
    };
    const reportsProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/reports",
        title: formatMessage(reportsMessages.reportPlural),
        className: location.pathname.startsWith("/reports") ? "active" : undefined,
    };
    const trendsProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/trends",
        title: formatMessage(salesMessages.salesTitle),
        className: location.pathname.startsWith("/trends") ? "active" : undefined,
    };
    const deliveriesProps = {
        ...baseLinkProps,
        tag: Link,
        to: "/deliveries",
        title: formatMessage(deliveryMessages.deliveryEntityPlural),
        className: location.pathname.startsWith("/deliveries") ? "active" : undefined,
    };
    // Unused pages
    // const bundlesProps = {
    //     tag: Link,
    //     to: "/bundles",
    //     title: formatMessage(uploadMessages.bundleEntityPlural),
    //     className: location.pathname.startsWith("/bundles") ? "active" : undefined,
    // };
    // const playlistsProps = {
    //     tag: Link,
    //     to: "/playlists",
    //     title: formatMessage(vendorMessages.playlistEntityPlural),
    //     className: location.pathname.startsWith("/playlists") ? "active" : undefined,
    // };
    // const statisticsProps = {
    //     tag: Link,
    //     to: "/statistics",
    //     title: formatMessage(messages.pageStatisticsTitle),
    //     className: location.pathname === "/statistics" ? "active" : undefined,
    // };
    let dropdownText = "User";
    if (user != null) {
        dropdownText = user.name;
    }
    if (organisation != null) {
        dropdownText += " (" + organisation.name + ")";
    }

    return (
        <header className="header mb-3" id="header">
            {/* Navbar */}
            <Navbar expand="md" container={false}>
                <NavbarBrand tag={Link} to="/" className="header__logo" container={false}>
                    <span className="visually-hidden">Watamusic</span>
                </NavbarBrand>
                <Nav className="right-nav ms-auto" navbar>
                    <Search />
                    <NavItem><LanguageSelect /></NavItem>
                </Nav>
                <NavbarToggler className={classNames({
                    "navbar-toggler--active": menuOpen,
                })} onClick={toggleMenu} />
            </Navbar>
            {/* Sidebar */}
            <Offcanvas
                isOpen={menuOpen}
                className="sidebar"
                toggle={toggleMenu}
                position="top"
                backdrop={false}
            >
                <Nav vertical color="white">
                    <NavItem className="sidebar__item-brand">
                        <Navbar container={false}>
                            <NavbarBrand tag={Link} to="/" className="sidebar__logo m-auto">
                                <span className="visually-hidden">Watamusic</span>
                            </NavbarBrand>
                        </Navbar>
                    </NavItem>
                    <NavItem>
                        <NavLink {...indexProps}>
                            <WaIcon name="home" />
                            <FormattedMessage {...uiMessages.home} />
                        </NavLink>
                    </NavItem>
                    {canCreateAlbum &&
                        <NavItem>
                            <NavLink {...albumCreateProps}>
                                <WaIcon name="add" />
                                <FormattedMessage {...messages.navCreateAlbumTitle} />
                            </NavLink>
                        </NavItem>
                    }
                    {canViewSales &&
                        <NavItem>
                            <NavLink {...trendsProps}>
                                <WaIcon name="bar-chart" />
                                    <FormattedMessage {...salesMessages.salesTitle} />
                            </NavLink>
                        </NavItem>
                    }
                    {canViewReport &&
                        <NavItem>
                            <NavLink {...statementsProps}>
                                <WaIcon name="document" />
                                <FormattedMessage {...reportsMessages.statementPlural} />
                            </NavLink>
                        </NavItem>
                    }
                    {canGenerateReport &&
                        <NavItem>
                            <NavLink {...reportsProps}>
                                <WaIcon name="report" />
                                <FormattedMessage {...reportsMessages.reportPlural} />
                            </NavLink>
                        </NavItem>
                    }
                    {canViewDelivery &&
                        <NavItem>
                            <NavLink {...deliveriesProps}>
                                <WaIcon name="network" />
                                <FormattedMessage {...deliveryMessages.deliveryEntityPlural} />
                            </NavLink>
                        </NavItem>
                    }
                    <div className="sidebar__spacer"></div>
                    <NavItem>
                        <span className="sidebar__user">
                            {user?.name ?? "User"}
                        </span>
                    </NavItem>
                    {canViewUser &&
                        <NavItem>
                            <NavLink {...usersProps}>
                                <WaIcon name="user" />
                                <FormattedMessage {...authMessages.userEntityPlural} />
                            </NavLink>
                        </NavItem>
                    }
                    <NavItem>
                        <LogoutButton tag={NavLink} />
                    </NavItem>
                </Nav>
            </Offcanvas>
        </header>
    );
};
